import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Ban } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { updateResource } from "@/lib/api";

// Click any table cell value to edit it inline (text / number / date / list),
// without opening the full edit form. Self-contained: updates + refreshes on its own.
// Pass `naField` to allow marking the value "Not Applicable" (stored as a boolean flag).
export default function InlineEdit({ row, path, field, type = "text", label, render, placeholder, naField }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const seed = () => (type === "list" ? (Array.isArray(row[field]) ? row[field].join("\n") : "") : (row[field] ?? ""));
  const [val, setVal] = useState(seed);
  const [saving, setSaving] = useState(false);
  const isNA = !!(naField && row[naField]);

  const persist = async (patch, msg = "Updated") => {
    setSaving(true);
    try {
      await updateResource(path, row.id, patch);
      qc.invalidateQueries({ queryKey: [path] });
      qc.invalidateQueries({ queryKey: ["reminders"] });
      qc.invalidateQueries({ queryKey: ["summary"] });
      toast.success(msg);
      setOpen(false);
    } catch {
      toast.error("Couldn't update");
    } finally {
      setSaving(false);
    }
  };

  const save = () => {
    let out = val;
    if (type === "number") out = val === "" ? 0 : Number(val);
    if (type === "list") out = String(val).split("\n").map((s) => s.trim()).filter(Boolean);
    persist({ [field]: out, ...(naField ? { [naField]: false } : {}) });
  };

  const naDisplay = <span className="text-sm text-gray-400 italic">N/A</span>;
  const display = isNA
    ? naDisplay
    : render
      ? render(row)
      : (row[field] === 0 || row[field] ? <span className="text-sm">{String(row[field])}</span> : <span className="text-gray-300">—</span>);

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setVal(seed()); }}>
      <PopoverTrigger asChild>
        <button data-testid={`inline-${field}`} className="text-left focus:outline-none group hover:underline decoration-dotted underline-offset-4">
          {display}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="rounded-none border-2 border-gray-900 w-72">
        <div className="space-y-3">
          <Label className="text-xs uppercase tracking-widest font-bold text-gray-500">{label}</Label>
          {type === "list" ? (
            <Textarea data-testid={`inline-input-${field}`} autoFocus rows={4} className="rounded-none border-2" value={val} placeholder={placeholder} onChange={(e) => setVal(e.target.value)} />
          ) : (
            <Input data-testid={`inline-input-${field}`} autoFocus type={type === "number" ? "number" : type === "date" ? "date" : "text"} className="rounded-none border-2" value={val} placeholder={placeholder} onChange={(e) => setVal(e.target.value)} />
          )}
          <Button data-testid={`inline-save-${field}`} onClick={save} disabled={saving} className="w-full rounded-none bg-gray-900 hover:bg-gray-700 text-white font-bold uppercase">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save
          </Button>
          {naField && (
            isNA ? (
              <Button data-testid={`inline-clear-na-${naField}`} variant="outline" onClick={() => persist({ [naField]: false }, "N/A cleared")} disabled={saving}
                className="w-full rounded-none border-2 font-bold uppercase text-xs">
                Clear "Not Applicable"
              </Button>
            ) : (
              <Button data-testid={`inline-set-na-${naField}`} variant="outline" onClick={() => persist({ [naField]: true }, "Marked N/A")} disabled={saving}
                className="w-full rounded-none border-2 font-bold uppercase text-xs">
                <Ban className="w-3.5 h-3.5 mr-1" /> Mark Not Applicable
              </Button>
            )
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
